import { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'react-feather';
import {
  CollectionSequence,
  CollectionSequenceCreate,
  CollectionSequenceStep,
  CollectionSequenceStepAssignee,
  CollectionTrigger,
} from '@dill/dill-shared';
import {
  DillModal,
  DillButton,
  DillInput,
  DillSelect,
  DillToggle,
  DillDropdown,
} from '../../../general/Dill';
import { EngagementClaimant } from './useCollectionBuyerEngagements';

const TASK_TYPE_OPTIONS = [
  { label: 'Call', value: 'CALL' },
  { label: 'Email', value: 'EMAIL' },
  { label: 'Text message', value: 'TEXT_MESSAGE' },
  { label: 'To-do', value: 'TO_DO' },
];
const TASK_TYPE_LABELS: Record<string, string> = {
  CALL: 'Call',
  EMAIL: 'Email',
  TEXT_MESSAGE: 'Text message',
  TO_DO: 'To-do',
};

const PRIORITY_OPTIONS = [
  { label: 'Low priority', value: 'LOW' },
  { label: 'Medium priority', value: 'MEDIUM' },
  { label: 'High priority', value: 'HIGH' },
];

const describeAssignee = (
  assignTo: CollectionSequenceStepAssignee,
  userOptions: { label: string; value: string }[]
): string => {
  if (assignTo.mode === 'ACCOUNT_OWNER') return "the customer's account owner";
  if (assignTo.mode === 'CUSTOMER_SALES_REP') return "the customer's sales rep";
  return userOptions.find((u) => u.value === assignTo.userId)?.label || 'a teammate';
};

const describeStep = (
  step: CollectionSequenceStep,
  index: number,
  userOptions: { label: string; value: string }[]
): string => {
  const timing =
    index === 0 ? 'immediately' : `${step.delayDays} day${step.delayDays === 1 ? '' : 's'} later`;
  return `${TASK_TYPE_LABELS[step.taskType]} task ${timing}, assigned to ${describeAssignee(
    step.assignTo,
    userOptions
  )}`;
};

const newStepId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `step-${Math.random()}`;

const defaultStep = (order: number): CollectionSequenceStep => ({
  id: newStepId(),
  order,
  delayDays: order === 0 ? 0 : 5,
  taskType: 'TO_DO',
  title: '',
  description: '',
  priority: 'MEDIUM',
  assignTo: { mode: 'ACCOUNT_OWNER' },
  dueInDays: null,
});

const RequiredMark = () => <span className="text-red-500">*</span>;

interface CollectionSequenceFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CollectionSequenceCreate) => void;
  initialData?: CollectionSequence | null;
  isLoading?: boolean;
  errorMessage?: string | null;
  supplierUsers: { id: string; firstName?: string | null; lastName?: string | null }[];
  supplierBuyers: { id: string; name?: string | null }[];
  triggers: Pick<CollectionTrigger, 'targetBuyerIds'>[];
  // When opened from a specific customer's page, pre-fills the customer list with this one
  // selected - still editable so more customers can be added.
  defaultBuyerId?: string;
  isBuyerAvailable: (buyerId: string, ignoreClaimant?: EngagementClaimant) => boolean;
}

export const CollectionSequenceFormModal = ({
  open,
  onClose,
  onSubmit,
  initialData,
  isLoading,
  errorMessage,
  supplierUsers,
  supplierBuyers,
  triggers,
  defaultBuyerId,
  isBuyerAvailable,
}: CollectionSequenceFormModalProps) => {
  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [steps, setSteps] = useState<CollectionSequenceStep[]>([defaultStep(0)]);
  const [buyerIds, setBuyerIds] = useState<string[]>([]);
  // Only start showing per-field validation errors once the user has tried to submit once - so an
  // empty form doesn't greet them with a wall of red before they've typed anything.
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  useEffect(() => {
    if (!open) return;
    setHasAttemptedSubmit(false);
    if (initialData) {
      setName(initialData.name);
      setIsActive(initialData.isActive);
      setSteps(initialData.steps);
      setBuyerIds(initialData.buyerIds);
    } else {
      setName('');
      setIsActive(true);
      setSteps([defaultStep(0)]);
      setBuyerIds(defaultBuyerId ? [defaultBuyerId] : []);
    }
  }, [open, initialData, defaultBuyerId]);

  const ignoreClaimant: EngagementClaimant | undefined = initialData
    ? { type: 'SEQUENCE_ENROLLED', id: initialData.id }
    : undefined;

  const userOptions = supplierUsers.map((u) => ({
    label: [u.firstName, u.lastName].filter(Boolean).join(' ') || 'Unnamed teammate',
    value: u.id,
  }));

  // Belt-and-suspenders alongside the engagement-map check below: read every trigger's own
  // `targetBuyerIds` directly, so a buyer who's genuinely targeted is caught even if their
  // `collectionBuyerEngagements` doc is somehow missing or hasn't caught up yet - the mutual-
  // exclusion rule shouldn't depend on that index always being perfectly in sync.
  const buyerIdsInAnyTrigger = new Set(triggers.flatMap((t) => t.targetBuyerIds));
  const isBuyerTrulyAvailable = (buyerId: string) =>
    isBuyerAvailable(buyerId, ignoreClaimant) && !buyerIdsInAnyTrigger.has(buyerId);

  // Buyers already engaged elsewhere (a different trigger's target list, or a different active
  // sequence enrollment) are left out of the picker entirely, so this sequence's customer list can
  // never violate the global mutual-exclusion rule.
  const availableBuyers = supplierBuyers.filter((b) => isBuyerTrulyAvailable(b.id));
  const unavailableBuyers = supplierBuyers.filter((b) => !isBuyerTrulyAvailable(b.id));
  const buyerOptions = availableBuyers.map((b) => ({
    label: b.name || 'Unnamed customer',
    value: b.id,
  }));

  // Guards against a currently-selected customer having become unavailable since the form loaded
  // (e.g. someone else just added them to a Trigger's target list in another tab/session) - the
  // picker only filters its own options list, so a stale selection carried over from `initialData`
  // needs its own check rather than relying on the picker to catch it.
  const unavailableSelectedBuyerIds = buyerIds.filter((id) => !isBuyerTrulyAvailable(id));

  const updateStep = (id: string, patch: Partial<CollectionSequenceStep>) => {
    setSteps((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  };

  const updateAssignee = (id: string, assignTo: CollectionSequenceStepAssignee) => {
    updateStep(id, { assignTo });
  };

  const addStep = () => {
    setSteps((prev) => [...prev, defaultStep(prev.length)]);
  };

  const removeStep = (id: string) => {
    setSteps((prev) => prev.filter((s) => s.id !== id).map((s, index) => ({ ...s, order: index })));
  };

  const isNameValid = name.trim().length > 0;
  const isStepTitleValid = (step: CollectionSequenceStep) => step.title.trim().length > 0;
  const isValid =
    isNameValid &&
    steps.length > 0 &&
    steps.every(isStepTitleValid) &&
    unavailableSelectedBuyerIds.length === 0;

  const showError = (fieldIsValid: boolean) => hasAttemptedSubmit && !fieldIsValid;

  const handleSubmit = () => {
    if (!isValid) {
      setHasAttemptedSubmit(true);
      return;
    }
    onSubmit({
      supplierId: '', // filled in by the caller
      name: name.trim(),
      isActive,
      buyerIds,
      steps: steps.map((s, index) => ({ ...s, order: index })),
      createdBy: null,
    } as CollectionSequenceCreate);
  };

  return (
    <DillModal
      open={open}
      onClose={onClose}
      title={initialData ? 'Edit collection sequence' : 'New collection sequence'}
      subtitle="The steps that run once a customer is enrolled, and who to enroll directly."
      maxWidth="640px"
      footer={
        <>
          <DillButton label="Cancel" variant="secondary" onClick={onClose} />
          <DillButton
            label={initialData ? 'Save changes' : 'Create sequence'}
            onClick={handleSubmit}
            isDisabled={!isValid}
            isLoading={isLoading}
          />
        </>
      }>
      {errorMessage && (
        <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 m-0">
          {errorMessage}
        </p>
      )}

      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-gray-600">
          Sequence name <RequiredMark />
        </label>
        <DillInput
          value={name}
          onChange={setName}
          placeholder="e.g. Call, email, text escalation"
          error={showError(isNameValid) ? 'Sequence name is required' : undefined}
        />
        {showError(isNameValid) && (
          <p className="text-[11px] text-red-500 m-0">Sequence name is required</p>
        )}
      </div>

      <div className="flex items-center justify-between">
        <label className="text-xs font-medium text-gray-600">Active</label>
        <DillToggle value={isActive} onChange={setIsActive} />
      </div>

      <div className="border-t border-gray-100 pt-3">
        <p className="text-xs font-semibold text-gray-700 mb-0.5">Enroll customers</p>
        <p className="text-xs text-gray-400 mt-0 mb-2">
          Optional - customers picked here are enrolled immediately and start running this sequence
          right away. A Collection Trigger can still enroll a customer even if they aren't picked
          here, and a sequence used only by triggers doesn't need anyone picked here at all.
        </p>
        <DillDropdown
          items={buyerOptions}
          value={null}
          isMulti
          showSelectedItems
          multiValue={buyerIds}
          onMultiSelectChange={(newValue) => setBuyerIds(newValue.map((v) => v.value))}
          placeholder="Search customers…"
          containerClassName="h-[36px]"
          showMinimalDesign
          numberOfItemsToShow={3}
        />
        {unavailableBuyers.length > 0 && (
          <p className="text-[11px] text-gray-400 mt-1 mb-0">
            Not shown - already on a trigger's target list or enrolled in another sequence
          </p>
        )}
      </div>

      <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-blue-400 m-0 mb-1">
          In plain English
        </p>
        <p className="text-xs text-blue-800 m-0 leading-relaxed">
          Once enrolled, this will: {describeStep(steps[0], 0, userOptions)}
          {steps.slice(1).map((step, i) => (
            <span key={step.id}>, then {describeStep(step, i + 1, userOptions)}</span>
          ))}
          .
        </p>
      </div>

      <div className="border-t border-gray-100 pt-3">
        <p className="text-xs font-semibold text-gray-700 mb-0.5">Steps</p>
        <p className="text-xs text-gray-400 mt-0 mb-2">
          Each step creates a task for your team. Step 1 fires as soon as the customer is enrolled;
          later steps fire N days after the PREVIOUS step's task was created.
        </p>
        <div className="flex flex-col gap-3">
          {steps.map((step, index) => (
            <div
              key={step.id}
              className="border border-gray-200 rounded-xl p-3 flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium text-gray-500 m-0">
                  Step {index + 1}
                  {index === 0
                    ? ' — immediately'
                    : ` — ${step.delayDays} day${
                        step.delayDays === 1 ? '' : 's'
                      } after step ${index}`}
                </p>
                {steps.length > 1 && (
                  <DillButton
                    variant="icon-red"
                    icon={<Trash2 size={12} />}
                    onClick={() => removeStep(step.id)}
                    height={24}
                    width={24}
                  />
                )}
              </div>

              {index > 0 && (
                <div>
                  <label className="text-xs font-medium text-gray-600">
                    Number of days after step {index + 1}
                  </label>
                  <DillInput
                    type="number"
                    value={String(step.delayDays)}
                    onChange={(v) =>
                      updateStep(step.id, { delayDays: Math.max(0, Number(v) || 0) })
                    }
                    placeholder="Days after previous step"
                  />
                  <p className="text-[11px] text-gray-400 mt-1 mb-0">
                    Counted from when step {index}'s task was created, not from enrollment.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-2">
                <DillSelect
                  value={step.taskType}
                  onChange={(v) => updateStep(step.id, { taskType: v as any })}
                  options={TASK_TYPE_OPTIONS}
                />
                <DillSelect
                  value={step.priority}
                  onChange={(v) => updateStep(step.id, { priority: v as any })}
                  options={PRIORITY_OPTIONS}
                />
              </div>

              <div>
                <DillInput
                  type="number"
                  value={step.dueInDays == null ? '' : String(step.dueInDays)}
                  onChange={(v) =>
                    updateStep(step.id, {
                      dueInDays: v.trim() === '' ? null : Math.max(0, Number(v) || 0),
                    })
                  }
                  placeholder="Task due in (days, optional)"
                />
                <p className="text-[11px] text-gray-400 mt-1 mb-0">
                  Sets a due date on the created task itself, counted from when it's created. Leave
                  blank for no due date.
                </p>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-600">
                  Task title <RequiredMark />
                </label>
                <DillInput
                  value={step.title}
                  onChange={(v) => updateStep(step.id, { title: v })}
                  placeholder="e.g. Email {{buyerName}} about {{amount}} past due"
                  error={showError(isStepTitleValid(step)) ? 'Task title is required' : undefined}
                />
                {showError(isStepTitleValid(step)) && (
                  <p className="text-[11px] text-red-500 mt-1 mb-0">Task title is required</p>
                )}
                <p className="text-[11px] text-gray-400 mt-1 mb-0">
                  {'{{buyerName}}'} and {'{{amount}}'} in the title or description get filled in
                  automatically for each customer.
                </p>
              </div>
              <textarea
                value={step.description ?? ''}
                onChange={(e) => updateStep(step.id, { description: e.target.value })}
                placeholder="Task description (optional)"
                className="h-16 px-3 py-2 text-sm rounded-lg border border-gray-300 w-full outline-none resize-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50"
              />

              <DillSelect
                value={step.assignTo.mode}
                onChange={(mode) => {
                  if (mode === 'SPECIFIC_USER') {
                    updateAssignee(step.id, { mode, userId: userOptions[0]?.value ?? '' });
                  } else if (mode === 'CUSTOMER_SALES_REP') {
                    updateAssignee(step.id, { mode, fallbackUserId: null });
                  } else {
                    updateAssignee(step.id, { mode } as CollectionSequenceStepAssignee);
                  }
                }}
                options={[
                  {
                    label: "Assign to account owner (customer's credit manager)",
                    value: 'ACCOUNT_OWNER',
                  },
                  { label: 'Assign to a specific teammate', value: 'SPECIFIC_USER' },
                  { label: "Assign to the customer's sales rep", value: 'CUSTOMER_SALES_REP' },
                ]}
              />
              {step.assignTo.mode === 'SPECIFIC_USER' && (
                <DillSelect
                  value={step.assignTo.userId}
                  onChange={(userId) => updateAssignee(step.id, { mode: 'SPECIFIC_USER', userId })}
                  options={userOptions}
                  placeholder="Choose a teammate"
                />
              )}
              {step.assignTo.mode === 'CUSTOMER_SALES_REP' && (
                <div className="flex flex-col gap-1">
                  <DillSelect
                    value={step.assignTo.fallbackUserId ?? ''}
                    onChange={(fallbackUserId) =>
                      updateAssignee(step.id, {
                        mode: 'CUSTOMER_SALES_REP',
                        fallbackUserId: fallbackUserId || null,
                      })
                    }
                    options={userOptions}
                    placeholder="Fallback teammate (optional)"
                  />
                  <p className="text-[11px] text-gray-400 m-0">
                    Resolved from the sales rep assigned to this customer on their profile (manage
                    the sales rep directory under Settings). Optional otherwise — leave blank to
                    leave the task unassigned rather than assigning it to nobody.
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
        <DillButton
          label="Add step"
          icon={<Plus size={12} />}
          variant="secondary"
          onClick={addStep}
          height={28}
          className="mt-3 w-full"
        />
      </div>
    </DillModal>
  );
};

export default CollectionSequenceFormModal;
